import { type ReactNode } from 'react';

export interface ShellNavItem {
  path: string;
  label: string;
  shortcut: string;
  icon: string;
}

const DEFAULT_NAV: ShellNavItem[] = [
  { path: '/command', label: 'Command', shortcut: 'C', icon: '◈' },
  { path: '/world', label: 'World', shortcut: 'W', icon: '◎' },
  { path: '/markets', label: 'Markets', shortcut: 'M', icon: '◫' },
  { path: '/capital', label: 'Capital', shortcut: 'K', icon: '◇' },
  { path: '/securities/acm', label: 'Securities', shortcut: 'S', icon: '▣' },
  { path: '/portfolio', label: 'Portfolio', shortcut: 'P', icon: '◉' },
  { path: '/theses', label: 'Theses', shortcut: 'T', icon: '◧' },
  { path: '/learn', label: 'Learn', shortcut: 'L', icon: '◐' },
  { path: '/glossary', label: 'Glossary', shortcut: 'B', icon: '◌' },
  { path: '/library', label: 'Library', shortcut: 'R', icon: '◫' },
  { path: '/operations', label: 'Operations', shortcut: 'O', icon: '◆' },
];

export function Shell({
  children,
  currentPath,
  onNavigate,
  onCommandPalette,
  navItems = DEFAULT_NAV,
  tagline = 'Map the game. Price the structure. Move the capital.',
  density = 'comfortable',
  onDensityChange,
  navCollapsed = false,
  onNavCollapsedChange,
  devBanner,
}: {
  children: ReactNode;
  currentPath: string;
  onNavigate: (path: string) => void;
  onCommandPalette: () => void;
  navItems?: ShellNavItem[];
  tagline?: string;
  density?: 'comfortable' | 'compact';
  onDensityChange?: (density: 'comfortable' | 'compact') => void;
  navCollapsed?: boolean;
  onNavCollapsedChange?: (collapsed: boolean) => void;
  devBanner?: ReactNode;
}) {
  const isActive = (path: string) =>
    currentPath === path ||
    (path.includes('/') && currentPath.startsWith(path.split('/').slice(0, 2).join('/')));

  return (
    <div className="atlas-app-shell" data-density={density} data-nav-collapsed={navCollapsed}>
      <nav className="atlas-app-nav" aria-label="Main navigation">
        <div className="atlas-app-brand">
          <span className="atlas-app-brand__mark" aria-hidden="true">
            C
          </span>
          {!navCollapsed && (
            <div>
              <strong>Cold State Atlas</strong>
              <small>{tagline}</small>
            </div>
          )}
          {onNavCollapsedChange && (
            <button
              type="button"
              className="atlas-nav-collapse"
              onClick={() => onNavCollapsedChange(!navCollapsed)}
              aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {navCollapsed ? '›' : '‹'}
            </button>
          )}
        </div>
        <ul className="atlas-app-nav__items">
          {navItems.map((item) => (
            <li key={item.path}>
              <button
                type="button"
                onClick={() => onNavigate(item.path)}
                className={isActive(item.path) ? 'is-active' : ''}
                aria-current={isActive(item.path) ? 'page' : undefined}
                title={navCollapsed ? item.label : undefined}
              >
                <span className="atlas-app-nav__symbol" aria-hidden="true">
                  {item.icon}
                </span>
                {!navCollapsed && (
                  <>
                    <span>{item.label}</span>
                    <kbd>G {item.shortcut}</kbd>
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
        {!navCollapsed && (
          <div className="atlas-app-nav__footer">
            <span>Private observatory</span>
            <small>Fixture environment · v0.1</small>
          </div>
        )}
      </nav>

      <div className="atlas-app-main">
        {devBanner}
        <header className="atlas-app-topbar">
          <div className="atlas-app-asof">
            <span className="atlas-live-dot" />
            Fixture mode <i /> Prior session <time>25 JUL 2026</time>
          </div>
          <div className="atlas-app-topbar__actions">
            {onDensityChange && (
              <button
                type="button"
                className="atlas-density-toggle"
                onClick={() => onDensityChange(density === 'comfortable' ? 'compact' : 'comfortable')}
                aria-label={`Switch to ${density === 'comfortable' ? 'compact' : 'comfortable'} density`}
              >
                {density === 'comfortable' ? 'Comfortable' : 'Compact'}
              </button>
            )}
            <button type="button" onClick={onCommandPalette} className="atlas-command-trigger">
              <span>Search or command</span>
              <kbd>⌘ K</kbd>
            </button>
          </div>
        </header>
        <main className="atlas-app-content">{children}</main>
      </div>
      <nav className="atlas-mobile-nav" aria-label="Mobile navigation">
        {navItems.slice(0, 5).map((item) => (
          <button
            type="button"
            key={item.path}
            className={isActive(item.path) ? 'is-active' : ''}
            onClick={() => onNavigate(item.path)}
          >
            <span aria-hidden="true">{item.icon}</span>
            <small>{item.label}</small>
          </button>
        ))}
        <button type="button" onClick={onCommandPalette}>
          <span aria-hidden="true">•••</span>
          <small>More</small>
        </button>
      </nav>
    </div>
  );
}

export { DEFAULT_NAV as NAV_ITEMS };
